/** Approval centre: pending cards first, filters, four grant scopes, audit note. */
import { useEffect, useMemo, useState } from 'react';
import type { ApprovalFilter } from '../lib/approval';
import {
  APPROVAL_FILTER_LABELS,
  approvalCounts,
  decisionScopeSummary,
  filterApprovals,
} from '../lib/approval';
import { ApprovalCard } from '../components/ApprovalCard';
import { ExecPanel } from '../components/ExecPanel';
import { Icon } from '../components/Icon';
import { StatusBanner } from '../components/StatusBanner';
import { useApp } from '../state/AppState';
import type { Route } from '../components/NavBar';

const STATUS_RANK: Record<string, number> = {
  pending: 0,
  granted: 1,
  denied: 2,
  expired: 3,
  revoked: 4,
};

const FILTER_ORDER: readonly ApprovalFilter[] = ['pending', 'done', 'all'];

export function ApprovalsPage({ onNavigate }: { onNavigate(route: Route): void }) {
  const {
    approvals,
    addDemoApproval,
    refreshApprovals,
    settings,
    paired,
    lastDecision,
    dismissLastDecision,
    liveExec,
    relayPlan,
    network,
  } = useApp();
  const [filter, setFilter] = useState<ApprovalFilter>('pending');

  // Decision feedback: keep it visible long enough to be read, then auto-dismiss.
  useEffect(() => {
    if (!lastDecision) return undefined;
    const timer = setTimeout(dismissLastDecision, 8000);
    return () => clearTimeout(timer);
  }, [lastDecision, dismissLastDecision]);

  const counts = useMemo(() => approvalCounts(approvals), [approvals]);
  const filtered = useMemo(() => filterApprovals(approvals, filter), [approvals, filter]);
  const sorted = useMemo(
    () =>
      [...filtered].sort((left, right) => {
        const rank = (STATUS_RANK[left.status] ?? 9) - (STATUS_RANK[right.status] ?? 9);
        return rank !== 0 ? rank : right.requested_at - left.requested_at;
      }),
    [filtered],
  );

  return (
    <div className="h-full overflow-y-auto">
      <StatusBanner />
      <div className="px-4 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-slate-100">审批中心</h1>
            <p className="text-[11px] text-slate-500">
              {counts.pending > 0 ? `${counts.pending} 个待处理审批` : '暂无待处理审批'} · 24h 过期
              · 全量审计
            </p>
          </div>
          <button
            type="button"
            onClick={refreshApprovals}
            className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-300"
          >
            <Icon name="refresh" className="h-3.5 w-3.5" />
            刷新
          </button>
        </div>

        <div className="mt-2 grid grid-cols-3 gap-1 rounded-xl border border-white/10 bg-white/[0.03] p-1">
          {FILTER_ORDER.map((option) => {
            const active = filter === option;
            const count =
              option === 'pending' ? counts.pending : option === 'done' ? counts.done : counts.all;
            return (
              <button
                key={option}
                type="button"
                onClick={() => setFilter(option)}
                aria-pressed={active}
                className={`rounded-lg py-1.5 text-xs transition ${
                  active ? 'bg-indigo-500/20 text-indigo-100' : 'text-slate-400'
                }`}
              >
                {APPROVAL_FILTER_LABELS[option]}{' '}
                <span className="text-[10px] text-slate-500">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3 px-4 pb-6">
        {!paired ? (
          <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-4 text-sm text-slate-300">
            <p>尚未配对设备。完成配对后才能接收 PC daemon 的审批请求。</p>
            <button
              type="button"
              onClick={() => onNavigate('pair')}
              className="mt-3 rounded-xl bg-indigo-500 px-4 py-2 text-xs font-medium text-white"
            >
              去配对
            </button>
          </section>
        ) : null}

        {settings.demoMode ? (
          <section className="flex items-center justify-between gap-3 rounded-2xl border border-indigo-400/20 bg-indigo-400/5 p-3">
            <p className="text-[11px] text-indigo-200">
              演示模式：审批不会真正执行。可生成新的演示审批卡验证四档授权与签名流程。
            </p>
            <button
              type="button"
              onClick={addDemoApproval}
              className="shrink-0 rounded-lg border border-indigo-400/40 bg-indigo-400/10 px-3 py-1.5 text-xs text-indigo-100"
            >
              生成演示审批
            </button>
          </section>
        ) : null}

        {!settings.demoMode ? (
          relayPlan.enabled ? (
            <section
              className={`flex items-center gap-2 rounded-2xl border p-3 text-[11px] ${
                network.relayConnected
                  ? 'border-emerald-400/20 bg-emerald-400/5 text-emerald-200'
                  : 'border-amber-400/20 bg-amber-400/5 text-amber-200'
              }`}
            >
              <Icon name="lock" className="h-3.5 w-3.5 shrink-0" />
              <p className="leading-relaxed">
                {network.relayConnected
                  ? 'live relay 已连接：审批请求经 E2E 密文中继实时到达；授权决定签名后经同一通道回传，执行结果在下方「实时执行」。'
                  : 'live relay 配置已就绪，等待 E2E 中继连接（审批请求到达前请保持网络在线）。'}
              </p>
            </section>
          ) : (
            <section className="flex items-center justify-between gap-3 rounded-2xl border border-amber-400/20 bg-amber-400/5 p-3 text-[11px] leading-relaxed text-amber-200">
              <p>{relayPlan.reason ?? 'live relay 未配置'}（live 审批/执行需要 E2E 中继）</p>
              <button
                type="button"
                onClick={() => onNavigate('settings')}
                className="shrink-0 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs text-amber-100"
              >
                去设置
              </button>
            </section>
          )
        ) : null}

        {lastDecision ? (
          <section
            className={`flex items-center justify-between gap-3 rounded-2xl border p-3 text-[11px] ${
              lastDecision.status === 'granted'
                ? 'border-emerald-400/25 bg-emerald-400/5 text-emerald-200'
                : 'border-rose-400/25 bg-rose-400/5 text-rose-200'
            }`}
          >
            <p className="leading-relaxed">
              {lastDecision.status === 'granted' ? '已批准' : '已拒绝'}「{lastDecision.tool}」·{' '}
              {decisionScopeSummary(lastDecision.decision)}
              {lastDecision.mode === 'live'
                ? lastDecision.queued
                  ? ' · 已进入 relay 离线队列，连接恢复后发送；daemon 验签后执行'
                  : ' · 签名决定已通过 E2E 中继发送；等待 daemon 验签执行并写审计'
                : lastDecision.status === 'granted'
                  ? ' · 签名已发送，PC daemon 验签后执行并写审计'
                  : ' · 拒绝已记入审计'}
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setFilter('done');
                  dismissLastDecision();
                }}
                className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] text-slate-200"
              >
                查看
              </button>
              <button
                type="button"
                onClick={dismissLastDecision}
                aria-label="关闭处理结果提示"
                className="rounded-lg p-1 text-current opacity-70"
              >
                <Icon name="close" className="h-3.5 w-3.5" />
              </button>
            </div>
          </section>
        ) : null}

        <ExecPanel execs={liveExec} now={Math.floor(Date.now() / 1000)} />

        {sorted.length === 0 ? (
          <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-6 text-center">
            <Icon name="shield" className="mx-auto h-8 w-8 text-slate-600" />
            <p className="mt-2 text-sm text-slate-300">
              {filter === 'pending'
                ? '没有待处理审批'
                : filter === 'done'
                  ? '还没有处理过的审批'
                  : '没有审批记录'}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {filter === 'pending'
                ? 'PC daemon 判定高风险操作时会在这里生成卡片；离线时审批会排队（24h 过期）。'
                : '批准/拒绝/过期都会保留在这里，方便复核审计。'}
            </p>
          </section>
        ) : (
          sorted.map((approval) => <ApprovalCard key={approval.approval_id} approval={approval} />)
        )}

        <section className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 text-[11px] leading-relaxed text-slate-500">
          <h2 className="text-xs font-medium text-slate-300">审批规则</h2>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            <li>四档授权：一次性 / 本任务 / 限时 15 分钟 / 永久白名单；高危操作不可永久白名单。</li>
            <li>高危操作需要二次确认；已配置 PIN 时还需要重新输入 PIN。</li>
            <li>签名绑定 approval_id + decision + args_hash + nonce，PC daemon 验签后才会执行。</li>
            <li>所有批准/拒绝都会写入哈希链审计；急停可吊销授权。</li>
            <li>
              live 模式：审批决定经 E2E 中继回传（签名绑定 approval_id + decision + args_hash +
              nonce）；卡片保持“待执行”，执行结果在「实时执行」显示。HTTPS 安全上下文是 E2E 前提。
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
